import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, MapPin, Users, Building2, Linkedin, Mail } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  const stats = [
    { value: "2.1K", label: "Active Interviews Processed", size: "large" },
    { value: "95%", label: "AI Successful Ratio", size: "medium" },
    { value: "1K+", label: "CAS Shield Interviews", size: "medium" },
    { value: "10+", label: "Registered Organizations", size: "small" },
    { value: "40+", label: "Team Members", size: "small" },
    { value: "UK", label: "Based Company", size: "small" }
  ];

  const team = [
    {
      name: "Ahmad Raza",
      role: "Chief Executive Officer",
      shortRole: "CEO",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
      linkedin: "#"
    },
    {
      name: "Sandra McLuckie",
      role: "Software Quality Manager",
      shortRole: "Quality",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
      linkedin: "#"
    },
    {
      name: "Dr. Mahtab Alam",
      role: "Infrastructure and Systems",
      shortRole: "Infrastructure",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
      linkedin: "#"
    },
    {
      name: "Humair Ahmed",
      role: "Business Consultant",
      shortRole: "Business",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
      linkedin: "#"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-lime-400/5 via-transparent to-lime-500/5"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 inline-flex items-center rounded-full border border-lime-500/30 bg-lime-500/10 px-4 py-1.5 text-sm font-medium text-lime-600 dark:text-lime-400" data-testid="about-badge">
                About Us
              </Badge>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight mb-6" data-testid="about-headline">
                Developed by
                <span className="text-lime-500"> Cloud Lab</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-6 leading-relaxed">
                Visainterviews.ai is developed by Cloud Lab, a software technology company based in the UK. We are a team of 40+ creative professionals driving innovations and continuous research & development.
              </p>
              <div className="flex items-center gap-2 text-muted-foreground mb-8">
                <MapPin className="w-5 h-5 text-lime-500" />
                <span>4 Crown Place, Liverpool Street, London, EC2A 4BT</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-lime-500 hover:bg-lime-600 text-white rounded-full px-8 gap-2 shadow-lg shadow-lime-500/20 hover:scale-105 transition-all"
                  onClick={() => navigate('/register')}
                  data-testid="about-cta-register"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => navigate('/contact')}
                  data-testid="about-cta-contact"
                >
                  Contact Us
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-gradient-to-r from-lime-400/20 to-lime-500/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Modern tech office workspace"
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-square"
                data-testid="about-hero-image"
              />
              <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border border-white/20 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-lime-500/20 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-lime-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Cloud Lab</p>
                    <p className="text-xs text-muted-foreground">Technology Company</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Bento Grid */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 inline-flex items-center rounded-full border border-lime-500/30 bg-lime-500/10 px-3 py-1 text-xs font-medium text-lime-600 dark:text-lime-400">
              Our Impact
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Numbers That Matter
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Large stat */}
            <Card className="col-span-2 row-span-2 bg-gradient-to-br from-lime-500 to-lime-600 text-white border-0 overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-testid="stat-large">
              <CardContent className="p-8 h-full flex flex-col justify-end relative">
                <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-white/10 blur-2xl"></div>
                <p className="text-6xl md:text-7xl font-heading font-bold">{stats[0].value}</p>
                <p className="text-lg text-lime-100 mt-2">{stats[0].label}</p>
              </CardContent>
            </Card>
            
            {/* Medium stats */}
            <Card className="col-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid="stat-medium-1">
              <CardContent className="p-6 flex flex-col justify-center h-full">
                <p className="text-4xl md:text-5xl font-heading font-bold text-lime-500">{stats[1].value}</p>
                <p className="text-sm text-muted-foreground mt-2">{stats[1].label}</p>
              </CardContent>
            </Card>
            
            <Card className="col-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid="stat-medium-2">
              <CardContent className="p-6 flex flex-col justify-center h-full">
                <p className="text-4xl md:text-5xl font-heading font-bold text-lime-500">{stats[2].value}</p>
                <p className="text-sm text-muted-foreground mt-2">{stats[2].label}</p>
              </CardContent>
            </Card>
            
            {/* Small stats */}
            {stats.slice(3).map((stat, index) => (
              <Card key={index} className="col-span-1 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid={`stat-small-${index}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 inline-flex items-center rounded-full border border-lime-500/30 bg-lime-500/10 px-3 py-1 text-xs font-medium text-lime-600 dark:text-lime-400">
              Our Team
            </Badge>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Meet Our Experts
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We are a team of 40+ creative professionals driving our innovations and continuous research & development
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-900 aspect-[3/4]"
                data-testid={`team-member-${index}`}
              >
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
                
                {/* Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center rounded-full bg-lime-500/90 px-3 py-1 text-xs font-medium text-white">
                    {member.shortRole}
                  </span>
                </div>
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform">
                  <h3 className="font-heading font-semibold text-xl mb-1">{member.name}</h3>
                  <p className="text-sm text-white/80 mb-4">{member.role}</p>
                  
                  {/* Social Links */}
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="ghost" className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white">
                      <Linkedin className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white">
                      <Mail className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Team collaboration"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                data-testid="mission-image"
              />
            </div>
            <div>
              <Badge className="mb-4 inline-flex items-center rounded-full border border-lime-500/30 bg-lime-500/10 px-3 py-1 text-xs font-medium text-lime-600 dark:text-lime-400">
                Our Mission
              </Badge>
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-6">
                We Love Working on Innovative Ideas
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  With over a decade of experience in software development, we deliver stable products and reliable support. Our AI-enabled interview practice platform is designed to help users confidently prepare for CAS Shield interviews, visa interviews, and IELTS speaking tests.
                </p>
                <p>
                  We understand the challenges students face when preparing for credibility interviews. That's why we've built a comprehensive platform that combines cutting-edge AI technology with realistic interview simulations.
                </p>
                <p className="font-medium text-foreground">
                  Save your months of effort and planning by making sure you pass the final stage.
                </p>
              </div>
              <Button 
                size="lg" 
                className="mt-8 bg-lime-500 hover:bg-lime-600 text-white rounded-full px-8 gap-2 shadow-lg shadow-lime-500/20 hover:scale-105 transition-all"
                onClick={() => navigate('/pricing')}
                data-testid="mission-cta"
              >
                View Pricing
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-6">
            Register Now — It's Worth It
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Save your months of effort and planning by making sure you pass the final stage. Join thousands of students who have successfully prepared with our platform.
          </p>
          <Button 
            size="lg" 
            className="bg-lime-500 hover:bg-lime-600 text-white rounded-full px-10 py-6 text-lg gap-2 shadow-lg shadow-lime-500/20 hover:scale-105 transition-all"
            onClick={() => navigate('/register')}
            data-testid="about-final-cta"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
